import {
	AfterViewInit,
	Component,
	ElementRef,
	inject,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewEncapsulation
} from "@angular/core";
import { LegacyPageEvent as PageEvent } from "@angular/material/legacy-paginator";
import { Router } from "@angular/router";
import { Store, select } from "@ngrx/store";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map, switchMap, take, takeUntil } from "rxjs/operators";
import { MatLegacyCheckboxChange as MatCheckboxChange } from "@angular/material/legacy-checkbox";
import {
	MatLegacySelect as MatSelect,
	MatLegacySelectChange as MatSelectChange
} from "@angular/material/legacy-select";

import { MenuService } from "@modules/config/_services/menu/menu.service";
import { HistoryLogType } from "@shared/components/history-log-side-panel/models/history-log-interface";
import { HistoryLogSidePanelService } from "@shared/components/history-log-side-panel/services/history-log-side-panel.service";
import { ModalLauncherService } from "@shared/components/wpay-ui/modal/modal-launcher.service";
import {
	cancelBookingDialog,
	providerMessage
} from "@shared/components/wpay-ui/modal/templates/cancel-booking/model/cancel-booking.interface";
import { Milestone, MilestoneStatusesValue, PayCycle } from "@shared/models/pay-cycle.interface";
import { PayGroup } from "@shared/models/pay-groups";
import { MilestoneReport, Payout, PayoutPagination, PayoutStatusName, Transaction } from "@shared/models/payouts.types";
import { SelectOption } from "@shared/models/select-option.interface";
import { PayoutsService } from "@shared/services/payouts/payouts.service";
import { ToastService } from "@shared/services/toast/toast.service";
import { SetStatusAction } from "@store/actions/statusNetPayment.action";
import { getGlobalDashboardFilterState, selectStatusNetPayment } from "@store/index";
import { AppState } from "@store/models/state.model";
import { PaymentsApiService } from "../../services/payments-api.service";
import { TppPaymentsApiService } from "../../services/tpp-payments-api.service";
import { cancelReport } from "../../tpp-payments/models/tpp-payment.interface";
import { MilestonesService } from "@shared/services/milestones/milestones.service";
import { ServiceProviderService } from "@shared/services/service-provider/service-provider.service";
import { AvailableServiceProvider, ProviderType } from "@shared/models/service-provider-types.interface";
import { CommonService } from "@modules/dashboard/services/common.service";
import { ExternalCrumb } from "@shared/models/breadcrumbs.interface";
import { UpdateHistoryStateInfoAction } from "@store/actions/historyStateInfo.action";
import { FilterMilestoneTypes } from "@modules/global-dashboard/models/global-dashboard-interface";

@Component({
	selector: "app-payouts",
	templateUrl: "./payouts.component.html",
	styleUrls: ["./payouts.component.scss"],
	encapsulation: ViewEncapsulation.None
})
export class PayoutsComponent implements OnInit, OnDestroy, AfterViewInit {
	payoutsPagination!: PayoutPagination;
	payGroup!: PayGroup;
	payCycleId = "";
	payCycle!: PayCycle;
	reProcessIds: string[] = [];
	bookingButtonState = "";
	payCycleMilestones!: Milestone[];
	payouts: Payout[] = [];
	allowReprocessBooking = false;
	isPaymentOrderComplete = false;
	payrollCycleId = "";

	//Transactions
	milestone!: Milestone;
	g2nMilestoneId = "";

	/**Pagination*/
	pageSize = 100;
	pageNumber = 0;

	milestoneReport!: MilestoneReport;
	runSelection!: SelectOption[];
	isCancelledRun = false;
	cancelReport: cancelReport[] = [];
	statusFilter: string[] = [];
	reProcesMapPayoutsAndTxId = new Map<string, string[]>();

	//Top card info
	selectedKPI = "";
	payCycleName = "";
	updating = true;
	title = "Net payment / ";
	subtitle = "";
	milestoneGroup = "NETS";
	flag = "GP";

	//Cancelation variables
	runName = "";
	cancelDate!: Date;
	cancellerName = "";
	reason = "";
	cancelId = "reset";

	showHistoryLogPanel = false;
	HistoryLogType = HistoryLogType;

	setFiltersToFixed = false;
	emitScroll = 0;
	allFailedTransaction: Transaction[] = [];
	allTransactionsAreSelected = false;
	allFallbackTransactionIds: string[] = [];
	showSelectOptions = false;
	providerType = ProviderType.API;
	serviceProviders: AvailableServiceProvider[] | undefined;
	isAllFailedTransactions = false;

	/**FOR BULK ACTIONS */
	isBulkSelected = false;
	bulkRelevantStatuses: string[] = [];

	/* Breadcrumbs */
	headerCrumb = "";
	externalCrumbs: ExternalCrumb[] = [];

	//Private

	private destroy$: Subject<void> = new Subject<void>();
	private dataSource: BehaviorSubject<Payout[]> = new BehaviorSubject<Payout[]>([]);

	payouts$: Observable<Payout[]> = this.dataSource.asObservable();

	@ViewChild("payoutContainer") payoutContainer!: ElementRef;
	@ViewChild("options") options!: MatSelect;

	private commonService = inject(CommonService);

	bulkSelected$ = this.commonService.isBulkSelected$;

	private payoutsService = inject(PayoutsService);
	private paymentApiService = inject(PaymentsApiService);
	private router = inject(Router);
	private tppPaymentsApiService = inject(TppPaymentsApiService);
	private modalLauncherService = inject(ModalLauncherService);
	private store = inject<Store<AppState>>(Store);
	private menuService = inject(MenuService);
	private historyLogService = inject(HistoryLogSidePanelService);
	private toast = inject(ToastService);
	private readonly milestonesService = inject(MilestonesService);
	public serviceProvider = inject(ServiceProviderService);
	private subscriptionsInitialized = false;

	constructor() {
		this.getServiceProviderList();
	}

	ngOnInit(): void {
		//if dashboard route
		if (this.router.url === "/global-dashboard/payouts") this.getSelectedKPI();
		else this.setHistory();
		this.menuService.setVisible(false); //hide sidenav

		this.historyLogService.transactionHistoryLogSubject.pipe(takeUntil(this.destroy$)).subscribe({
			next: res => {
				if (res.payoutId !== "") {
					this.showHistoryLogPanel = true;
				} else {
					this.showHistoryLogPanel = false;
				}
			}
		});

		this.bulkSelected$.pipe(takeUntil(this.destroy$)).subscribe(isBulkSelected => {
			this.isBulkSelected = isBulkSelected;
		});
	}

	ngAfterViewInit() {
		this.attachScrollListener();
	}

	attachScrollListener(): void {
		const scrollContainer = this.payoutContainer?.nativeElement;
		if (scrollContainer) {
			scrollContainer.addEventListener("scroll", this.onScroll.bind(this));
		}
	}

	onScroll(event: Event): void {
		const scrollContainer = event.target as HTMLElement;
		this.emitScroll = scrollContainer.scrollTop;
	}

	setHistory(): void {
		if (
			history?.state?.payCycleId &&
			history?.state?.payGroup &&
			history?.state?.milestone &&
			this.hasG2NMilestone(history?.state?.milestones)
		) {
			this.payGroup = history.state.payGroup;
			this.payCycle = history.state.payCycle;
			this.payCycleName = history.state.payCycleName;
			this.payCycleId = history.state.payCycleId;
			this.payCycleMilestones = history.state.milestones;
			this.milestone = history.state.milestone;
			this.payrollCycleId = history.state.payrollCycleId;

			this.getMilestoneReport(this.milestone?.id ? this.milestone.id : "");
			this.headerCrumb = history.state.payrollCycleId
				? `${history.state.payGroup.customer.name} - ${history.state.payCycle.name} - ${history.state.payrollCycleId}, GROUP ${history.state.milestone?.group}`
				: `${history.state.payGroup.customer.name} - ${history.state.payCycle.name}, GROUP ${history.state.milestone?.group}`;

			this.externalCrumbs = [
				{
					crumb: "Net Payment",
					dropDownData: [
						{
							id: this.payCycleMilestones.find(
								milestone =>
									milestone.type === FilterMilestoneTypes.PAYMENTORDERS &&
									milestone.group === this.milestone.group
							)?.id!,
							name: "Payment Orders"
						},
						{
							id: this.payCycleMilestones.find(
								milestone =>
									milestone.type === FilterMilestoneTypes.FUNDING &&
									milestone.group === this.milestone.group
							)?.id!,
							name: "Funding Date"
						}
					]
				}
			];

			if (this.payGroup?.data && this.payGroup?.legalEntity?.data && this.milestone?.group) {
				this.setupTopCardInfo();
			}

			this.applyDeeplinkPayoutStatusFilter();

			this.processed();
			this.setupSubscriptions();
		} else {
			this.router.navigate(["/global-dashboard"]);
			this.toast.showError("Something went wrong while trying to load the page. Please try again later.");
		}
	}

	/**
	 * Route user to other milestone pages
	 * @param id: milestone ID
	 */
	routeToMilestonePage(id?: string): void {
		const selectedMilestone = this.payCycleMilestones.find(milestone => milestone.id === id)!;
		let route = "global-dashboard";

		const g2nRoute =
			selectedMilestone.status === MilestoneStatusesValue.PENDING ||
			selectedMilestone.status === MilestoneStatusesValue.COMPLETE
				? `/${route}/payment-orders`
				: `/${route}/file-history`;

		switch (selectedMilestone.type) {
			case FilterMilestoneTypes.PAYMENTORDERS:
				this.router.navigate([g2nRoute], {
					state: {
						payGroup: this.payGroup,
						payCycleId: this.payCycleId,
						payCycleName: this.payCycleName,
						payCycle: this.payCycle,
						milestones: this.payCycleMilestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId,
						previousUrl: this.router.url
					}
				});

				this.store.dispatch(
					new UpdateHistoryStateInfoAction({
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						payCycleName: this.payCycle.name,
						milestones: this.payCycleMilestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId
					})
				);
				break;

			case FilterMilestoneTypes.FUNDING:
				this.router.navigate([`/${route}/funding-date`], {
					state: {
						payGroup: this.payGroup,
						payCycleId: this.payCycleId,
						payCycleName: this.payCycleName,
						milestones: this.payCycleMilestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId
					}
				});

				this.store.dispatch(
					new UpdateHistoryStateInfoAction({
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						payCycleName: this.payCycle.name,
						milestones: this.payCycleMilestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId
					})
				);
				break;

			default:
				break;
		}
	}

	hasG2NMilestone(milestones: Milestone[]): boolean {
		const g2nMilestone = milestones?.find(
			(milestone: Milestone) => milestone.type === FilterMilestoneTypes.PAYMENTORDERS
		);

		if (g2nMilestone?.id) {
			this.g2nMilestoneId = g2nMilestone?.id;
			return true;
		} else {
			return false;
		}
	}

	setupTopCardInfo() {
		this.title = this.title + this.payGroup.data.name;
		this.subtitle = this.commonService.formatMilestonePagesSubtitles(
			this.payGroup.legalEntity.data.name,
			this.payCycleName,
			this.payrollCycleId,
			this.milestone.group
		);
		this.flag = this.payGroup.legalEntity.data.country;
		this.milestoneGroup = this.milestone.group;
	}

	private applyDeeplinkPayoutStatusFilter(): void {
		if (!Object.prototype.hasOwnProperty.call(history?.state || {}, "payoutStatusFilter")) {
			return;
		}

		this.store.dispatch(
			new SetStatusAction({
				statusPayment: history.state.payoutStatusFilter ?? []
			})
		);
	}

	getSelectedKPI(): void {
		this.store.pipe(take(1), select(getGlobalDashboardFilterState)).subscribe(state => {
			if (state?.globalDashboard?.kpiSelected !== undefined) {
				this.selectedKPI = state.globalDashboard.kpiSelected;
				if (state.globalDashboard.kpiSelected === "Failed Transactions") {
					this.title = "Failed transactions / ";
					this.store.dispatch(
						new SetStatusAction({
							statusPayment: ["FAILED,ATTEMPTS_LIMIT_EXCEEDED", "FAILED_TO_BOOK", "PROCESSING,PENDING"]
						})
					);
				}
				this.setHistory();
			} else {
				this.router.navigate(["/global-dashboard"]);
			}
		});
	}

	/**
	 * Check payment provider and handle payout with empty transactions
	 * @param payouts
	 * @returns: payment provider name or empty string
	 */
	checkPaymentProvider(payouts: Payout[]): string {
		return (
			payouts.find(tx => tx.transactions !== undefined && tx.transactions.length > 0)?.transactions![0]
				?.paymentProvider ?? ""
		);
	}

	setupSubscriptions(): void {
		if (this.subscriptionsInitialized) {
			return;
		}
		this.subscriptionsInitialized = true;

		this.store.pipe(takeUntil(this.destroy$), select(selectStatusNetPayment)).subscribe(({ statusPayment }) => {
			this.statusFilter = statusPayment ? statusPayment : [];
			this.getPayouts();
		});

		this.payoutsService
			.reprocessIdsObservable()
			.pipe(takeUntil(this.destroy$))
			.subscribe((res: Map<string, string[]>) => {
				if (res.size > 0) {
					this.bookingButtonState = "reprocess";
					this.allTransactionsAreSelected =
						this.allFailedTransaction.length === this.getTotalReprocessTransactions(res);
				} else {
					this.allTransactionsAreSelected = false;
					this.getServiceProviderType(this.payouts.length > 0 ? this.checkPaymentProvider(this.payouts) : "");
					this.payoutsService.getBookingState(this.payouts, this.providerType);
				}
			});

		this.tppPaymentsApiService.reloadPayoutAfterCancelling.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.payoutsService.reloadPayoutData.emit();
		});

		this.payoutsService.reloadPayoutData.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.processed();
			this.refresh();
			this.updating = true;
			setTimeout(() => {
				this.resetCardInfo();
			}, 6000);
		});
	}

	/**
	 * Get all the service providers list available
	 */
	getServiceProviderList(): void {
		this.serviceProvider
			.getProviders()
			.pipe(take(1), takeUntil(this.destroy$))
			.subscribe(provider => {
				this.serviceProviders = provider;
			});
	}

	/**
	 * return service provider type (API / NON API)
	 * @param providerId: Provider ID used in transaction
	 */
	getServiceProviderType(providerId = ""): void {
		this.providerType =
			this.serviceProviders?.find(provider => provider.id === providerId)?.type ?? ProviderType.API;
	}

	/**
	 * @param value: reprocess transaction values
	 * @returns total number of needed to reprocess transactions
	 */
	getTotalReprocessTransactions(values: Map<string, string[]>) {
		let totalTransactions = 0;

		values.forEach(val => {
			totalTransactions += val.length;
		});

		return totalTransactions;
	}

	getMilestoneReport(milestoneId: string): void {
		this.updating = true;
		if (milestoneId) {
			this.tppPaymentsApiService
				.getMilestoneReport(milestoneId)
				.pipe(takeUntil(this.destroy$))
				.subscribe(res => {
					this.milestoneReport = res;
					this.updating = false;
				});
		} else {
			this.milestoneReport = this.tppPaymentsApiService.DEFAULT_MILESTONE_VALUES;
			this.updating = false;
		}
	}

	resetCardInfo(): void {
		this.getMilestoneReport(this.milestone?.id ? this.milestone.id : "");
	}

	getPayouts(selectAll = false) {
		this.payoutsService
			.getPayoutsWithPagination(
				this.payGroup.id,
				this.payCycleId,
				this.statusFilter,
				this.pageSize.toString(),
				this.pageNumber.toString()
			)
			.pipe(
				take(1),
				takeUntil(this.destroy$),
				map(response => {
					this.payoutsPagination = response;
					const payouts: Payout[] = response.items;
					this.payouts = payouts;

					this.allFailedTransaction = [];
					this.allFallbackTransactionIds = [];

					if (payouts.length > 0) {
						payouts.forEach(payout => {
							this.allFallbackTransactionIds = this.allFallbackTransactionIds.concat(
								this.paymentApiService.getTransactionsIdsForPayoutsWithPaidToFallbackStatus(
									payout.transactions!
								)
							);
							payout.transactions = this.paymentApiService.filterTransactions(payout.transactions!);
							payout.status.name = this.payoutsService.getPayoutStatus(payout, payout.transactions);

							if (payout.status.name !== PayoutStatusName.INACTIVE) {
								payout.transactions.forEach(transaction => {
									if (this.isBulkSelected) {
										if (this.bulkRelevantStatuses.includes(transaction.status.name)) {
											this.allFailedTransaction.push(transaction);
										}
									} else {
										if (this.paymentApiService.canReprocess(transaction.status.name))
											this.allFailedTransaction.push(transaction);
									}
								});
							}
						});

						this.isAllFailedTransactions = payouts.length === this.allFailedTransaction.length;

						if (selectAll) {
							this.payoutsService.selectAllReprocessIds(this.allFailedTransaction);
							this.bookingButtonState = "reprocess";
						} else {
							this.payoutsService.clearReprocessIds();
							this.getServiceProviderType(this.checkPaymentProvider(payouts));
							this.bookingButtonState = this.payoutsService.getBookingState(payouts, this.providerType);
						}
					} else {
						this.bookingButtonState = "inactive";
					}

					this.dataSource.next(response.items);
				}),
				switchMap(() => this.milestonesService.getMilestones(this.payCycleId))
			)
			.subscribe(milestones => {
				const isMilestoneCompleted =
					milestones.filter(
						milestone =>
							milestone.type === FilterMilestoneTypes.PAYMENTORDERS &&
							milestone.status === MilestoneStatusesValue.COMPLETE
					).length > 0;

				/** SOME STATUSES */
				const isAnyProcessed = this.payouts.some(payout => payout.status.name === PayoutStatusName.PROCESSED);
				const isAnyProcessing = this.payouts.some(payout => payout.status.name === PayoutStatusName.PROCESSING);
				const isAnyPending = this.payouts.some(payout => payout.status.name === PayoutStatusName.PENDING);
				const isAnyFailedToBook = this.payouts.some(
					payout => payout.status.name === PayoutStatusName.FAILED_TO_BOOK
				);
				const isAnyFailed = this.payouts.some(payout => payout.status.name === PayoutStatusName.FAILED);
				const isAnyBooked = this.payouts.some(payout => payout.status.name === PayoutStatusName.BOOKED);

				const isAnyInactive = this.payouts.some(payout => payout.status.name === PayoutStatusName.INACTIVE);

				/** EVERY STATUSES */
				const isAllBooked =
					isAnyBooked &&
					this.payouts
						.filter(payout => payout.status.name !== PayoutStatusName.INACTIVE)
						.every(payout => payout.status.name === PayoutStatusName.BOOKED);
				const isAllFailed = this.payouts
					.filter(payout => payout.status.name !== PayoutStatusName.INACTIVE)
					.every(payout => payout.status.name === PayoutStatusName.FAILED);
				const isAllProcessed =
					this.payouts.length > 0 &&
					this.payouts.every(payout => payout.status.name === PayoutStatusName.PROCESSED);
				const isAllFailedToBook =
					this.payouts.length > 0 &&
					this.payouts.every(payout => payout.status.name === PayoutStatusName.FAILED_TO_BOOK);

				const isAllFailedAndFailedToBook =
					this.payouts.length > 0 &&
					this.payouts.every(
						payout =>
							payout.status.name === PayoutStatusName.FAILED ||
							payout.status.name === PayoutStatusName.FAILED_TO_BOOK
					);

				const isAllInactive =
					this.payouts.length > 0 &&
					this.payouts.every(payout => payout.status.name === PayoutStatusName.INACTIVE);

				this.isPaymentOrderComplete = isMilestoneCompleted;

				if (
					isMilestoneCompleted &&
					!isAllBooked &&
					!isAllFailed &&
					!isAllFailedToBook &&
					!isAllProcessed &&
					!isAnyProcessed &&
					!isAnyProcessing &&
					!isAnyPending &&
					!isAllInactive
				) {
					this.bookingButtonState = "active";
				}

				if (isAnyBooked && (isAnyFailed || isAnyFailedToBook)) {
					this.bookingButtonState = "reprocess";
				}

				if (
					(isAnyFailedToBook && isAnyInactive && !isAnyProcessed) ||
					(isAnyFailedToBook && isAnyBooked) ||
					isAllFailedAndFailedToBook ||
					isAllFailedToBook ||
					isAllFailed
				) {
					this.bookingButtonState = "cancel";
				}

				if (this.providerType !== ProviderType.API && isAllProcessed) {
					this.bookingButtonState = "completed";
				}

				if (this.providerType !== ProviderType.API && isAllBooked) {
					this.bookingButtonState = "complete-payments";
				}

				if (
					this.providerType === ProviderType.API &&
					(!isMilestoneCompleted || isAllProcessed || isAllInactive)
				) {
					this.bookingButtonState = "inactive";
				}

				if (this.providerType === ProviderType.API && isAllBooked) {
					this.bookingButtonState = "cancel";
				}

				/** To check if user can reprocess booking when ticked checkbox */
				if (isAnyProcessed) {
					this.allowReprocessBooking = isAnyFailed;
				} else {
					this.allowReprocessBooking =
						isAllFailed || isAnyFailed || isAnyFailedToBook || isAllFailedAndFailedToBook;
				}
			});
	}

	getCancelledPayouts(cancelledId: string) {
		this.tppPaymentsApiService
			.getCancelledNetPayoutsWithPagination(cancelledId, this.pageSize, this.pageNumber)
			.pipe(
				takeUntil(this.destroy$),
				map(response => {
					this.payoutsPagination = response;
					this.bookingButtonState = "inactive";
					this.dataSource.next(response.items);
				})
			)
			.subscribe();
	}

	setupCancelBanner(cancelId: string) {
		const currentReport = this.cancelReport.find(report => report.id === cancelId)!;
		this.runName = this.runSelection.find(run => run.value === cancelId)!.text;
		this.cancelDate = currentReport.canceledAt;
		this.cancellerName = currentReport.canceledBy;
		this.reason = currentReport.reason;
	}

	processed(): void {
		this.reProcessIds = [];
		this.getCancelReport(this.payGroup?.id, this.g2nMilestoneId);
	}

	refresh(): void {
		if (this.cancelId !== "reset") {
			this.getCancelledPayouts(this.cancelId);
		} else {
			this.resetCardInfo();
			this.getPayouts();
		}
	}

	changeRun(cancelId: string) {
		this.cancelId = cancelId;

		if (cancelId !== "reset") {
			this.getCancelledPayouts(cancelId);
			this.setupCancelBanner(cancelId);
			this.isCancelledRun = true;
		} else {
			this.getPayouts();
			this.isCancelledRun = false;
		}
	}

	getCancelReport(paygroupId: string, g2nMilestoneId: string) {
		this.runSelection = [
			{
				text: "Current Run",
				value: "reset"
			}
		];
		this.tppPaymentsApiService
			.getCancelReport(paygroupId, g2nMilestoneId, 1000, 0)
			.pipe(take(1))
			.subscribe(cancelledRuns => {
				if (cancelledRuns.items.length) {
					cancelledRuns.items.sort(
						(a, b) => new Date(a.canceledAt).getTime() - new Date(b.canceledAt).getTime()
					);

					this.cancelReport = cancelledRuns.items;

					let i = 0;
					cancelledRuns.items.forEach(cancelledRun => {
						i++;

						this.runSelection.push({
							text: "Cancelled run " + i,
							value: cancelledRun.id
						});
					});
				}
			});
	}

	selectAllFailedTransactions(event: MatSelectChange): void {
		if (event.value === "visible") {
			this.payoutsService.selectAllReprocessIds(this.allFailedTransaction);
			this.allTransactionsAreSelected = true;
		} else if (event.value === "all") {
			if (this.pageSize >= this.payoutsPagination.totalCount) {
				this.payoutsService.selectAllReprocessIds(this.allFailedTransaction);
			} else {
				this.pageSize = this.payoutsPagination.totalCount;
				this.pageNumber = 0;
				this.getPayouts(true);
			}
		}
	}

	clearSelection(event: MatCheckboxChange, manualClear = false): void {
		this.options.value = null;
		this.allTransactionsAreSelected = false;
		if (!event.checked || manualClear) {
			this.payoutsService.clearReprocessIds();
		} else {
			this.openSelect();
		}
	}

	cancelBooking() {
		const providerMessages: providerMessage[] = [
			{
				provider: "Either Transfermate, Checkout or Ebury",
				message: "After canceling this batch, it is necessary to contact your Service Provider"
			}
		];

		const cancelBooking: cancelBookingDialog = {
			milestoneId: this.g2nMilestoneId,
			providerMessages: providerMessages
		};

		this.modalLauncherService.showCancelBookingModal(cancelBooking);
	}

	paginate(pagination: PageEvent) {
		this.pageSize = pagination.pageSize;
		this.pageNumber = pagination.pageIndex;
		if (this.isBulkSelected) {
			this.clearSelection({} as MatCheckboxChange, true);
		}
		this.refresh();
	}

	openSelect() {
		this.showSelectOptions = true;
		if (this.options) this.options.open();
	}

	/**
	 * Gets bulk selected relevant statuses
	 * @param statuses: bulk relevant statuses
	 */
	getBulkRelevantStatuses(statuses: string[]): void {
		this.bulkRelevantStatuses = statuses;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
